import React, { useState } from "react";
import api, { apiErrorText } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  ArrowDownLeft,
  ArrowUpRight,
  History,
  MoreHorizontal,
  RefreshCw,
} from "lucide-react";
import { fmtUSD } from "../lib/format";
import { toast } from "sonner";
import WalletHistorySheet from "./WalletHistorySheet";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Action menu + dialogs for a wallet row.
 * - Chime gets an "Add cash → CashApp" item that pushes a deposit into the linked CashApp.
 * - All wallets get Withdraw, Update balance and View history.
 */
export default function WalletActions({ wallet, onMutate, actions, processor = false }) {
  const allActions = actions || (
    wallet.type === "chime" || wallet.type === "paypal"
      ? ["addcash", "withdraw", "balance", "history"]
      : ["withdraw", "balance", "history"]
  );
  const allow = (k) => allActions.includes(k);
  const chargePct = Number(wallet.charge_percent) || 0;
  const usdtToUsd = (n) => {
    const div = 1 - chargePct / 100;
    if (div <= 0) return 0;
    return (Number(n) || 0) / div;
  };
  const [historyOpen, setHistoryOpen] = useState(false);
  // Withdraw
  const [wOpen, setWOpen] = useState(false);
  const [wAmt, setWAmt] = useState("");
  const [wRate, setWRate] = useState("");
  const [wNotes, setWNotes] = useState("");
  // Balance update
  const [bOpen, setBOpen] = useState(false);
  const [bVal, setBVal] = useState("");
  const [bNotes, setBNotes] = useState("");
  // Add cash (chime → cashapp)
  const [aOpen, setAOpen] = useState(false);
  const [aAmt, setAAmt] = useState("");
  const [aTarget, setATarget] = useState("");

  const allowAddCash = allow("addcash") &&
    (wallet.type === "chime" || wallet.type === "paypal");
  const addCashLabel = "Add cash → CashApp";

  const openWithdraw = async () => {
    setWOpen(true);
    setWAmt("");
    setWNotes("");
    if (processor) {
      setWRate(null);
      return;
    }
    try {
      const r = await api.get("/rates");
      const row = r.data.find((x) => x.wallet_type === wallet.type);
      setWRate(row?.rate ?? 1);
    } catch {
      setWRate(1);
    }
  };

  const submitWithdraw = async () => {
    try {
      const amt = Number(wAmt);
      if (!amt || amt <= 0) return toast.error("Enter a valid amount");
      await api.post("/transactions", {
        wallet_id: wallet.id,
        kind: "withdrawal",
        amount_usd: amt,
        rate: Number(wRate) || null,
        notes: wNotes,
      });
      toast.success("Withdrawal recorded");
      setWOpen(false);
      onMutate?.();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openBalance = () => {
    setBOpen(true);
    setBVal(String(wallet.balance ?? 0));
    setBNotes("");
  };

  const submitBalance = async () => {
    try {
      const nb = Number(bVal);
      if (Number.isNaN(nb)) return toast.error("Enter a valid balance");
      await api.post("/transactions", {
        wallet_id: wallet.id,
        kind: "balance_update",
        new_balance: nb,
        notes: bNotes,
      });
      toast.success("Balance updated");
      setBOpen(false);
      onMutate?.();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openAddCash = () => {
    setAOpen(true);
    setAAmt("");
    setATarget(wallet.linked_cashapps?.[0]?.id || "");
  };

  const submitAddCash = async () => {
    try {
      if (!aTarget) return toast.error("Link a CashApp to this wallet first");
      const amt = Number(aAmt);
      if (!amt || amt <= 0) return toast.error("Enter a valid amount");
      await api.post("/transactions", {
        wallet_id: wallet.id,
        kind: "deposit",
        amount_usd: amt,
        target_wallet_id: aTarget,
        notes: `From ${wallet.type === "paypal" ? "PayPal" : "Chime"} · ${wallet.name}`,
      });
      toast.success("Cash added to CashApp");
      setAOpen(false);
      onMutate?.();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
            title="Quick actions"
            data-testid={`actions-wallet-${wallet.id}`}
          >
            <MoreHorizontal size={15} strokeWidth={1.7} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {allowAddCash && (
            <DropdownMenuItem
              onClick={openAddCash}
              data-testid={`add-cash-wallet-${wallet.id}`}
            >
              <ArrowDownLeft size={13} className="mr-2" /> {addCashLabel}
            </DropdownMenuItem>
          )}
          {allow("withdraw") && (
            <DropdownMenuItem
              onClick={openWithdraw}
              data-testid={`withdraw-wallet-${wallet.id}`}
            >
              <ArrowUpRight size={13} className="mr-2" /> Withdraw
            </DropdownMenuItem>
          )}
          {allow("balance") && (
            <DropdownMenuItem
              onClick={openBalance}
              data-testid={`update-balance-wallet-${wallet.id}`}
            >
              <RefreshCw size={13} className="mr-2" /> Update balance
            </DropdownMenuItem>
          )}
          {allow("history") && <DropdownMenuSeparator />}
          {allow("history") && (
            <DropdownMenuItem
              onClick={() => setHistoryOpen(true)}
              data-testid={`history-wallet-${wallet.id}`}
            >
              <History size={13} className="mr-2" /> View history
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Add cash → CashApp (chime only) */}
      <Dialog open={aOpen} onOpenChange={setAOpen}>
        <DialogContent data-testid="add-cash-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add cash to CashApp</DialogTitle>
            <DialogDescription>
              From <span className="font-medium">{wallet.name}</span>. Deposits to the linked CashApp.
            </DialogDescription>
          </DialogHeader>
          {wallet.linked_cashapps?.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5">
                <Label>Linked CashApp</Label>
                <Select value={aTarget} onValueChange={setATarget}>
                  <SelectTrigger data-testid="add-cash-cashapp-select">
                    <SelectValue placeholder="Select CashApp" />
                  </SelectTrigger>
                  <SelectContent>
                    {wallet.linked_cashapps.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Amount (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={aAmt}
                  onChange={(e) => setAAmt(e.target.value)}
                  data-testid="add-cash-amount-input"
                />
              </div>
            </div>
          ) : (
            <div className="rounded-md border hairline bg-amber-50/60 px-3 py-3 text-[13px] text-amber-800">
              No CashApp linked to this {wallet.type === "paypal" ? "PayPal" : "Chime"} yet.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitAddCash}
              disabled={!wallet.linked_cashapps?.length}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-add-cash-btn"
            >
              Add cash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw */}
      <Dialog open={wOpen} onOpenChange={setWOpen}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Withdraw from {wallet.name}
            </DialogTitle>
            <DialogDescription>
              Current balance{" "}
              <span className="font-mono">
                {processor
                  ? `${Number(wallet.balance || 0).toLocaleString("en-US", {
                      maximumFractionDigits: 4,
                    })} USDT`
                  : fmtUSD(wallet.balance || 0)}
              </span>
            </DialogDescription>
          </DialogHeader>
          {processor ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount (USDT) *</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={wAmt}
                  onChange={(e) => setWAmt(e.target.value)}
                  data-testid="withdraw-amount-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>≈ USD value</Label>
                <Input
                  value={fmtUSD(usdtToUsd(wAmt))}
                  disabled
                  className="font-mono text-emerald-700"
                />
                <div className="text-[11px] text-zinc-500 font-mono">
                  USDT ÷ (1 − {chargePct}%) = USD
                </div>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Input
                  value={wNotes}
                  onChange={(e) => setWNotes(e.target.value)}
                  placeholder="Optional"
                  data-testid="withdraw-notes-input"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={wAmt}
                  onChange={(e) => setWAmt(e.target.value)}
                  data-testid="withdraw-amount-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Rate (USD → USDT)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={wRate}
                  onChange={(e) => setWRate(e.target.value)}
                  data-testid="withdraw-rate-input"
                />
                <div className="text-[11px] text-zinc-500 font-mono">
                  ≈ {fmtUSD(Number(wAmt || 0))} →{" "}
                  {(Number(wAmt || 0) * Number(wRate || 0)).toFixed(4)} USDT
                </div>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Input
                  value={wNotes}
                  onChange={(e) => setWNotes(e.target.value)}
                  placeholder="Optional"
                  data-testid="withdraw-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setWOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitWithdraw}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-withdraw-btn"
            >
              <ArrowUpRight size={13} className="mr-1" /> Record withdrawal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update balance */}
      <Dialog open={bOpen} onOpenChange={setBOpen}>
        <DialogContent data-testid="update-balance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Update balance</DialogTitle>
            <DialogDescription>
              {processor
                ? `Set the current USDT balance for ${wallet.name}. Charge ${chargePct}% applied.`
                : `Set the leftover balance for ${wallet.name} after add-cash and withdrawals.`}
            </DialogDescription>
          </DialogHeader>
          {processor ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Current balance (USDT)</Label>
                <Input
                  value={Number(wallet.balance || 0).toLocaleString("en-US", {
                    maximumFractionDigits: 4,
                  })}
                  disabled
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label>New balance (USDT) *</Label>
                <Input
                  type="number"
                  step="0.0001"
                  value={bVal}
                  onChange={(e) => setBVal(e.target.value)}
                  data-testid="update-balance-value-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Δ USDT</Label>
                <Input
                  value={(
                    (Number(bVal) || 0) - Number(wallet.balance || 0)
                  ).toLocaleString("en-US", { maximumFractionDigits: 4 })}
                  disabled
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Δ ≈ USD value</Label>
                <Input
                  value={fmtUSD(
                    usdtToUsd((Number(bVal) || 0) - Number(wallet.balance || 0))
                  )}
                  disabled
                  className="font-mono text-emerald-700"
                />
                <div className="text-[11px] text-zinc-500 font-mono">
                  Δ USDT ÷ (1 − {chargePct}%) = USD
                </div>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Input
                  value={bNotes}
                  onChange={(e) => setBNotes(e.target.value)}
                  placeholder="e.g. End-of-day reconcile"
                  data-testid="update-balance-notes-input"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Current balance</Label>
                <Input
                  value={fmtUSD(wallet.balance || 0)}
                  disabled
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label>New balance (USD) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={bVal}
                  onChange={(e) => setBVal(e.target.value)}
                  data-testid="update-balance-value-input"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes</Label>
                <Input
                  value={bNotes}
                  onChange={(e) => setBNotes(e.target.value)}
                  placeholder="e.g. End-of-day reconcile"
                  data-testid="update-balance-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitBalance}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-update-balance-btn"
            >
              <RefreshCw size={13} className="mr-1" /> Save balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WalletHistorySheet
        wallet={historyOpen ? wallet : null}
        onClose={() => setHistoryOpen(false)}
        onMutate={onMutate}
      />
    </>
  );
}
